import { IconMaximize } from "@/assets/icons/geist/IconMaximize";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { StartSequenceStepIcon } from "@/assets/icons/geist/StartSequenceStepIcon";
import {
    isStaleMachineSession,
    stepAutomationsState,
    stepCloneState,
    stepDevcontainerState,
    stepSecretsState,
    stepStartState,
    StepState,
} from "@/components/environments/environment-start-steps-state";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent } from "@/components/flexkit/Collapsible";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import type { PropsWithTestId } from "@/components/podkit/lib/testid";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { Tooltip } from "@/components/Tooltip";
import { VisibleOnActive } from "@/components/ui/VisibleOnActive";
import { durationBetweenDates, formatMediumTime, timeAgo } from "@/format/time";
import { useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { PrebuildLogsModal } from "@/components/projects/PrebuildLogsModal";
import { usePrebuild } from "@/queries/prebuild-queries";
import {
    Agents,
    useAgentExecutionsByEnvironment,
    useCreateAgentExecution,
    useSendToAgentExecution,
} from "@/queries/agent-queries";
import { useEnvironmentComputeDescription } from "@/hooks/use-environment-compute-description";
import { TrackLocations, type TrackLocation } from "@/hooks/use-segment";
import {
    getRepoUrlFromInitializer,
    toPlainRunnerEnvironmentClass,
    useStartEnvironment,
    useUpdateEnvironment,
    type PlainEnvironment,
    type PlainRunnerEnvironmentClass,
} from "@/queries/environment-queries";
import { useGetEnvironmentClass } from "@/queries/runner-configuration-queries.ts";
import {
    useIsUserAuthenticatedWithRunner,
    useRunner,
    type AuthenticatedWithRunnerResponse,
    type PlainRunner,
} from "@/queries/runner-queries.ts";
import { useListOrganizationSecrets, useListUserSecrets } from "@/queries/secret-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { SCMAuthenticationModal } from "@/routes/environments/create/SCMAuthentication.tsx";
import type { SectionLogLine } from "@/routes/environments/log-streams/log-groups";
import { environmentLogGroupUrl, isBlankEnvironment } from "@/utils/environment";
import { formatError } from "@/utils/errors";
import { pluralize } from "@/utils/strings";
import { getDevcontainerFailurePrompt } from "@/utils/built-in-prompts";
import { hasLLMCapabilities } from "@/utils/runner-capabilities";
import {
    AgentCodeContext,
    AgentExecution_Phase,
    SendToAgentExecutionRequest,
    UserInputBlock,
    UserInputBlock_TextInput,
} from "gitpod-next-api/gitpod/v1/agent_pb";
import {
    EnvironmentPhase,
    EnvironmentStatus_ContentPhase,
    type EnvironmentSpec_Secret,
} from "gitpod-next-api/gitpod/v1/environment_pb.ts";
import { RunnerKind } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { Timestamp, type PlainMessage } from "@bufbuild/protobuf";
import { useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

export const StepStart: FC<{ environment: PlainEnvironment; runner: PlainRunner; latestLogLine?: SectionLogLine }> = ({
    environment,
    runner,
    latestLogLine,
}) => {
    const navigate = useNavigate();

    const state = useMemo(() => stepStartState(environment), [environment]);

    const title = useMemo(() => {
        const machine = runner.kind == RunnerKind.LOCAL ? "local virtual machine" : "remote virtual machine";
        switch (state) {
            case StepState.Failure:
            case StepState.Waiting:
            case StepState.Empty:
                return `Start ${machine}`;
            case StepState.Running:
                return `Starting ${machine}`;
            case StepState.Warning:
            case StepState.Success:
                return `Started ${machine}`;
        }
    }, [runner.kind, state]);

    const description = useEnvironmentComputeDescription(environment);
    const subtitle = [description?.runnerName, description?.clazz?.displayName, description?.clazz?.description]
        .filter(Boolean)
        .join("\u00A0\u00A0•\u00A0\u00A0");

    const text = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }
        return [environment.status?.machine?.failureMessage, environment.status?.machine?.warningMessage]
            .filter(Boolean)
            .join("\n");
    }, [environment]);

    const onViewLogs = useCallback(
        () => navigate(environmentLogGroupUrl(environment.id, latestLogLine?.id)),
        [environment.id, latestLogLine?.id, navigate],
    );

    const viewLogs = useMemo(() => {
        const isShowingLatestLogLine = !!latestLogLine && state === StepState.Running;
        if (state == StepState.Waiting || isShowingLatestLogLine) {
            return;
        }
        if (onViewLogs) {
            return (
                <Button size="sm" variant="secondary" onClick={onViewLogs} data-tracking-id="show-logs-start-step">
                    Logs
                </Button>
            );
        }
    }, [latestLogLine, onViewLogs, state]);

    return (
        <Step
            environmentId={environment.id}
            title={title}
            subtitle={subtitle}
            logLine={latestLogLine}
            text={text}
            state={state}
            hoverActions={viewLogs}
        />
    );
};

export const StepClone: FC<{ environment: PlainEnvironment; latestLogLine?: SectionLogLine }> = ({
    environment,
    latestLogLine,
}) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const startEnvironment = useStartEnvironment();

    const state = useMemo(() => stepCloneState(environment), [environment]);
    const title = useMemo(() => {
        const isBlank = isBlankEnvironment(environment);

        switch (state) {
            case StepState.Failure:
            case StepState.Waiting:
            case StepState.Empty:
                return isBlank ? "Initialize repository" : "Clone repository";
            case StepState.Running:
                return isBlank ? "Initializing repository" : "Cloning repository";
            case StepState.Warning:
            case StepState.Success:
                return isBlank ? "Initialized repository" : "Cloned repository";
        }
    }, [state, environment]);

    const text = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }
        return [environment.status?.content?.failureMessage, environment.status?.content?.warningMessage]
            .filter(Boolean)
            .join("\n");
    }, [environment]);

    const repoURL = useMemo(() => {
        return getRepoUrlFromInitializer(environment.spec?.content?.initializer);
    }, [environment]);

    const isFailed = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }
        return environment.status?.content?.phase == EnvironmentStatus_ContentPhase.FAILED;
    }, [environment]);
    const { data: authCheckResponse } = useIsUserAuthenticatedWithRunner(environment.metadata?.runnerId, repoURL, {
        refetchUntilAuthenticated: true,
        sessionId: environment.spec?.machine?.session,

        // Auth check should be polled only when content is in a failed state.
        enabled: isFailed,
    });

    const isAuthenticationRequired = isFailed && authCheckResponse?.type == "AuthenticationRequired";
    const [showScmAuthModal, setShowScmAuthModal] = useState<SCMAuthenticationModalProps | undefined>();

    const { data: clazz } = useGetEnvironmentClass(environment.spec?.machine?.class);

    const onViewLogs = useCallback(
        () => navigate(environmentLogGroupUrl(environment.id, latestLogLine?.id)),
        [environment.id, latestLogLine?.id, navigate],
    );

    const viewLogs = useMemo(() => {
        const isShowingLatestLogLine = !!latestLogLine && state === StepState.Running;
        if (state == StepState.Waiting || isShowingLatestLogLine) {
            return;
        }
        if (onViewLogs) {
            return (
                <Button size="sm" variant="secondary" onClick={onViewLogs} data-tracking-id="show-logs-clone-step">
                    Logs
                </Button>
            );
        }
    }, [latestLogLine, onViewLogs, state]);

    const reAuthenticate = useMemo(() => {
        if (!isAuthenticationRequired) {
            return;
        }
        return (
            <Button
                size="sm"
                variant="primary"
                onClick={() => {
                    if (!authCheckResponse || !clazz) {
                        return;
                    }
                    setShowScmAuthModal({
                        repoURL: repoURL || "",
                        clazz: toPlainRunnerEnvironmentClass(clazz),
                        authResponse: authCheckResponse,
                        onClose: () => setShowScmAuthModal(undefined),
                        onAuthSuccess: () => {
                            setShowScmAuthModal(undefined);
                        },
                        "data-track-location": TrackLocations.EnvironmentSCMAuthenticationModal,
                    });
                }}
                data-track-label="true"
                data-tracking-id="reauthenticate-clone-step"
            >
                Re-Authenticate
            </Button>
        );
    }, [authCheckResponse, clazz, isAuthenticationRequired, repoURL]);

    return (
        <>
            <Step
                environmentId={environment.id}
                title={title}
                subtitle={environment.status?.content?.git?.cloneUrl}
                text={text}
                state={state}
                logLine={latestLogLine}
                actions={reAuthenticate}
                hoverActions={viewLogs}
            />
            {showScmAuthModal && (
                <AuthenticationModal
                    {...showScmAuthModal}
                    onClose={() => setShowScmAuthModal(undefined)}
                    onAuthSuccess={async () => {
                        setShowScmAuthModal(undefined);

                        // Restart environment when authentication is successful and the environment is stopped.
                        if (environment.status?.phase === EnvironmentPhase.STOPPED) {
                            try {
                                await startEnvironment.mutateAsync({
                                    environmentId: environment.id,
                                    skipPolicyCheck: true,
                                });
                            } catch (error) {
                                toast({
                                    title: "We couldn't start your environment. Sorry about that! Please retry.",
                                    description: formatError(error),
                                });
                            }
                        }
                    }}
                />
            )}
        </>
    );
};

export const StepStartDevcontainer: FC<{ environment: PlainEnvironment; latestLogLine?: SectionLogLine }> = ({
    environment,
    latestLogLine,
}) => {
    const navigate = useNavigate();

    const updateEnvironment = useUpdateEnvironment();
    const createAgentExecution = useCreateAgentExecution();
    const sendToAgentExecution = useSendToAgentExecution();
    const { toast } = useToast();
    const { state, canRebuild } = useMemo(() => stepDevcontainerState(environment), [environment]);
    const { value: isOnaEnabled } = useFlagOnaAIEnabled();
    const { data: runner } = useRunner(environment.metadata?.runnerId);
    const runnerSupportsLLM = hasLLMCapabilities(runner);
    const [isAskingOna, setIsAskingOna] = useState(false);
    const [askOnaCooldown, setAskOnaCooldown] = useState(false);
    const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Clear cooldown timeout on unmount
    useEffect(() => {
        return () => {
            if (cooldownTimeoutRef.current) {
                clearTimeout(cooldownTimeoutRef.current);
            }
        };
    }, []);

    // Check if there's an active agent execution for this environment
    const { data: inEnvironmentAgentExecution } = useAgentExecutionsByEnvironment({
        environmentId: environment.id,
        agentId: Agents.InEnvironmentOna.id,
    });
    const isAgentRunning = useMemo(() => {
        if (!inEnvironmentAgentExecution) {
            return false;
        }
        const phase = inEnvironmentAgentExecution.status?.phase;
        return phase === AgentExecution_Phase.RUNNING || phase === AgentExecution_Phase.PENDING;
    }, [inEnvironmentAgentExecution]);

    const title = useMemo(() => {
        switch (state) {
            case StepState.Failure:
            case StepState.Waiting:
            case StepState.Empty:
                return "Start dev container";
            case StepState.Running:
                return "Starting dev container";
            case StepState.Success:
            case StepState.Warning:
                return "Started dev container";
        }
    }, [state]);

    const failureMessage = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }
        return environment.status?.devcontainer?.failureMessage;
    }, [environment]);

    const text = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }
        return [environment.status?.devcontainer?.failureMessage, environment.status?.devcontainer?.warningMessage]
            .filter(Boolean)
            .join("\n");
    }, [environment]);

    const [hint, rebuildButtonLabel] = canRebuild
        ? ["The dev container file is out of sync with the current environment", "Rebuild"]
        : [undefined, undefined];

    const onClickRebuild = useCallback(async () => {
        await updateEnvironment.mutateAsync(
            {
                req: {
                    environmentId: environment.id,
                    spec: {
                        devcontainer: {
                            session: uuidv4(),
                        },
                    },
                },
            },
            {
                onError(error) {
                    toast({
                        title: "Failed to trigger rebuild of the dev container",
                        description: formatError(error),
                    });
                },
            },
        );
    }, [toast, updateEnvironment, environment.id]);

    const onClickAskOna = useCallback(async () => {
        const prompt = getDevcontainerFailurePrompt(failureMessage);

        setIsAskingOna(true);
        try {
            // Use existing agent execution or create a new one
            let agentExecutionId = inEnvironmentAgentExecution?.id;
            if (!agentExecutionId) {
                const result = await createAgentExecution.mutateAsync({
                    agentId: Agents.InEnvironmentOna.id,
                    codeContext: new AgentCodeContext({
                        context: {
                            case: "environmentId",
                            value: environment.id,
                        },
                    }),
                });
                agentExecutionId = result.agentExecutionId;
            }

            // Send the message
            await sendToAgentExecution.mutateAsync(
                new SendToAgentExecutionRequest({
                    agentExecutionId: agentExecutionId,
                    userInput: new UserInputBlock({
                        id: uuidv4(),
                        input: {
                            case: "text",
                            value: new UserInputBlock_TextInput({
                                content: prompt,
                            }),
                        },
                    }),
                }),
            );

            toast({
                title: "Ona is analyzing the issue",
                description: "Check the AI sidebar for updates",
            });

            // Start 30-second cooldown
            setAskOnaCooldown(true);
            cooldownTimeoutRef.current = setTimeout(() => setAskOnaCooldown(false), 30000);
        } catch (error) {
            toast({
                title: "Failed to ask Ona for help",
                description: formatError(error),
            });
        } finally {
            setIsAskingOna(false);
        }
    }, [failureMessage, environment.id, inEnvironmentAgentExecution, createAgentExecution, sendToAgentExecution, toast]);

    const onViewLogs = useCallback(
        () => navigate(environmentLogGroupUrl(environment.id, latestLogLine?.id)),
        [environment.id, latestLogLine?.id, navigate],
    );

    const subtitle = useMemo(() => {
        const contentPath = environment.status?.content?.contentLocationInMachine || "";
        const devcontainerPath = environment.status?.devcontainer?.devcontainerFilePath || "";
        return devcontainerPath.slice(contentPath.length).replace(/^\//, "");
    }, [environment]);

    const viewLogs = useMemo(() => {
        const isShowingLatestLogLine = !!latestLogLine && state === StepState.Running;
        if (state == StepState.Waiting || isShowingLatestLogLine) {
            return;
        }
        if (onViewLogs) {
            return (
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={onViewLogs}
                    data-tracking-id="show-logs-devcontainer-step"
                >
                    Logs
                </Button>
            );
        }
    }, [latestLogLine, onViewLogs, state]);

    const showAskOnaButton = state === StepState.Failure && isOnaEnabled && runnerSupportsLLM && !isAgentRunning;

    return (
        <Step
            environmentId={environment.id}
            title={title}
            subtitle={subtitle}
            text={text}
            hint={hint}
            logLine={latestLogLine}
            state={state}
            actions={
                <>
                    {showAskOnaButton && (
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={onClickAskOna}
                            loading={isAskingOna}
                            disabled={askOnaCooldown}
                            data-track-label="true"
                            data-tracking-id="ask-ona-devcontainer-step"
                        >
                            <OnaLettermark className="mr-1 size-4" />
                            Ask Ona
                        </Button>
                    )}
                    {canRebuild && (
                        <Button
                            size="sm"
                            variant="primary"
                            loading={updateEnvironment.isPending}
                            onClick={onClickRebuild}
                            data-track-label="true"
                            data-tracking-id="rebuild-devcontainer-step"
                        >
                            {rebuildButtonLabel}
                        </Button>
                    )}
                </>
            }
            hoverActions={viewLogs}
            hasNext={false}
        />
    );
};

export const StepAutomations: FC<{ environment: PlainEnvironment; latestLogLine?: SectionLogLine }> = ({
    environment,
    latestLogLine,
}) => {
    const navigate = useNavigate();
    const state = useMemo(() => stepAutomationsState(environment), [environment]);

    const title = useMemo(() => {
        switch (state) {
            case StepState.Failure:
            case StepState.Waiting:
            case StepState.Empty:
                return "Load automations";
            case StepState.Running:
                return "Loading automations";
            case StepState.Warning:
            case StepState.Success:
                return "Loaded automations";
        }
    }, [state]);

    const onViewLogs = useCallback(
        () => navigate(environmentLogGroupUrl(environment.id, latestLogLine?.id)),
        [environment.id, latestLogLine?.id, navigate],
    );

    const subtitle = useMemo(() => {
        if (state == StepState.Empty) {
            return "No automations file";
        }
        return environment.status?.automationsFile?.automationsFilePath;
    }, [state, environment.status?.automationsFile?.automationsFilePath]);

    const text = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }
        return [
            environment.status?.automationsFile?.failureMessage,
            environment.status?.automationsFile?.warningMessage,
        ]
            .filter(Boolean)
            .join("\n");
    }, [environment]);

    const viewLogs = useMemo(() => {
        if (state == StepState.Waiting) {
            return;
        }
        if (onViewLogs) {
            return (
                <Button
                    size="sm"
                    variant="secondary"
                    onClick={onViewLogs}
                    data-tracking-id="show-logs-automations-step"
                >
                    Logs
                </Button>
            );
        }
    }, [onViewLogs, state]);
    return (
        <Step
            environmentId={environment.id}
            title={title}
            subtitle={subtitle}
            text={text}
            state={state}
            hoverActions={viewLogs}
        />
    );
};

export const StepSecrets: FC<{ environment: PlainEnvironment; onShowSecrets: () => void }> = ({
    environment,
    onShowSecrets,
}) => {
    const { data: userSecrets, isPending: userSecretsPending } = useListUserSecrets();
    const { data: user } = useAuthenticatedUser();
    const { data: organizationSecrets, isPending: orgSecretsPending } = useListOrganizationSecrets(
        user?.organizationId,
    );
    const state = useMemo(() => stepSecretsState(environment), [environment]);
    const isPending = userSecretsPending || orgSecretsPending;

    const secretsBySource = useMemo(() => {
        const result = {
            organization: [] as PlainMessage<EnvironmentSpec_Secret>[],
            project: [] as PlainMessage<EnvironmentSpec_Secret>[],
            user: [] as PlainMessage<EnvironmentSpec_Secret>[],
        };
        if (!userSecrets) {
            return result;
        }

        for (const secret of environment.spec?.secrets || []) {
            if (userSecrets.secrets.some((s) => s.id === secret.sourceRef)) {
                result.user.push(secret);
            } else if (organizationSecrets?.secrets.some((s) => s.id === secret.sourceRef)) {
                result.organization.push(secret);
            } else {
                result.project.push(secret);
            }
        }
        return result;
    }, [environment.spec?.secrets, userSecrets, organizationSecrets]);

    const title = useMemo(() => {
        if (isPending) {
            return "Loading secrets";
        }
        switch (state) {
            case StepState.Failure:
            case StepState.Waiting:
            case StepState.Empty:
                return "Load secrets";
            case StepState.Running:
                return "Loading secrets";
            case StepState.Warning:
            case StepState.Success:
                return "Loaded secrets";
        }
    }, [state, isPending]);

    const subtitle = useMemo(() => {
        const organizationSecretsCount = secretsBySource.organization.length;
        const projectSecretsCount = secretsBySource.project.length;
        const userSecretsCount = secretsBySource.user.length;
        const specSecretsCount = organizationSecretsCount + projectSecretsCount + userSecretsCount;

        if (state === StepState.Empty) {
            return "No secrets configured";
        }

        if (state === StepState.Success && specSecretsCount > 0) {
            const parts = [];
            if (organizationSecretsCount > 0) {
                parts.push(organizationSecretsCount + " " + pluralize(organizationSecretsCount, "organization secret"));
            }
            if (projectSecretsCount > 0) {
                parts.push(projectSecretsCount + " " + pluralize(projectSecretsCount, "project secret"));
            }
            if (userSecretsCount > 0) {
                parts.push(userSecretsCount + " " + pluralize(userSecretsCount, "user secret"));
            }
            return parts.join(" \u00B7 ");
        }

        // Show the same breakdown by source when there are warnings/failures
        if (state === StepState.Warning && specSecretsCount > 0) {
            const parts = [];
            if (organizationSecretsCount > 0) {
                parts.push(organizationSecretsCount + " " + pluralize(organizationSecretsCount, "organization secret"));
            }
            if (projectSecretsCount > 0) {
                parts.push(projectSecretsCount + " " + pluralize(projectSecretsCount, "project secret"));
            }
            if (userSecretsCount > 0) {
                parts.push(userSecretsCount + " " + pluralize(userSecretsCount, "user secret"));
            }
            return parts.join(" \u00B7 ");
        }

        return;
    }, [state, secretsBySource]);

    const text = useMemo(() => {
        if (isStaleMachineSession(environment)) {
            return;
        }

        const secrets = environment.status?.secrets ?? [];
        const failedSecrets = secrets.filter(
            (s) => s.phase === EnvironmentStatus_ContentPhase.FAILED || s.failureMessage,
        );
        const warningSecrets = secrets.filter((s) => s.warningMessage);

        const messages = [];

        // Add failure messages (only show specific error messages)
        for (const secret of failedSecrets) {
            if (secret.failureMessage) {
                messages.push(`${secret.secretName}: ${secret.failureMessage}`);
            }
        }

        // Add warning messages
        for (const secret of warningSecrets) {
            if (secret.warningMessage) {
                messages.push(`${secret.secretName}: ${secret.warningMessage}`);
            }
        }

        if (!messages.length) {
            return;
        }

        return messages.join("\n");
    }, [environment]);

    const hoverActions = useMemo(() => {
        if (state === StepState.Success || state === StepState.Warning) {
            return (
                <Button size="sm" variant="secondary" onClick={onShowSecrets} data-tracking-id="view-secrets-step">
                    View
                </Button>
            );
        }
        return;
    }, [onShowSecrets, state]);

    const actions = useMemo(() => {
        if (state === StepState.Empty) {
            return (
                <ExternalLink href="https://ona.com/docs/ona/projects/project-secrets" iconSize="sm">
                    Learn about secrets
                </ExternalLink>
            );
        }
        return;
    }, [state]);

    return (
        <Step
            environmentId={environment.id}
            title={title}
            subtitle={subtitle}
            text={text}
            state={state}
            data-testid="secrets-step"
            actions={actions}
            hoverActions={hoverActions}
        />
    );
};

export const StepPrebuild: FC<{ prebuildId?: string; environmentId: string }> = ({ prebuildId, environmentId }) => {
    const { data: prebuild, error } = usePrebuild(prebuildId);
    const [showLogsModal, setShowLogsModal] = useState(false);

    // Don't render if no prebuild ID
    if (!prebuildId) {
        return null;
    }

    // Prebuild was deleted (404) - show minimal step without logs
    if (error) {
        return (
            <Step
                environmentId={environmentId}
                title="Started from prebuild"
                state={StepState.Success}
                data-testid="prebuild-step"
            />
        );
    }

    // Loading state - don't show anything to avoid layout shift
    if (!prebuild) {
        return null;
    }

    const createdAt = prebuild.metadata?.createdAt ? new Timestamp(prebuild.metadata.createdAt).toDate() : undefined;
    const completedAt = prebuild.status?.completionTime
        ? new Timestamp(prebuild.status.completionTime).toDate()
        : undefined;

    const duration = createdAt && completedAt ? durationBetweenDates(createdAt, completedAt) : undefined;
    const relativeTime = createdAt ? timeAgo(createdAt) : undefined;
    const warningMessage = prebuild.status?.warningMessage;

    // Build subtitle: "Ran 2 hours ago · 3m 42s"
    const subtitleParts: string[] = [];
    if (relativeTime) {
        subtitleParts.push(`Ran ${relativeTime}`);
    }
    if (duration) {
        subtitleParts.push(duration);
    }
    const subtitle = subtitleParts.join(" · ") || undefined;

    const state = warningMessage ? StepState.Warning : StepState.Success;

    const viewLogsButton = (
        <Button
            size="sm"
            variant="secondary"
            onClick={() => setShowLogsModal(true)}
            data-tracking-id="show-logs-prebuild-step"
        >
            Logs
        </Button>
    );

    return (
        <>
            <Step
                environmentId={environmentId}
                title="Started from prebuild"
                subtitle={subtitle}
                text={warningMessage}
                state={state}
                hoverActions={viewLogsButton}
                data-testid="prebuild-step"
            />
            {showLogsModal && (
                <PrebuildLogsModal prebuildId={prebuildId} open={showLogsModal} onOpenChange={setShowLogsModal} />
            )}
        </>
    );
};

const Step: FC<
    {
        environmentId: string;
        title: string;
        subtitle?: string;
        text?: string;
        hint?: string;
        logLine?: SectionLogLine;
        state: StepState;
        actions?: ReactNode;
        hoverActions?: ReactNode;
        hasNext?: boolean;
    } & PropsWithClassName &
        PropsWithTestId
> = ({
    environmentId,
    title,
    subtitle,
    text,
    hint,
    logLine,
    actions,
    hoverActions,
    state,
    className,
    "data-testid": dataTestId,
    hasNext = true,
}) => {
    const secondLine = text;

    const navigate = useNavigate();

    const onLogLineClick = useCallback(
        () => navigate(environmentLogGroupUrl(environmentId, logLine?.id)),
        [environmentId, logLine?.id, navigate],
    );

    return (
        <div
            className={cn("group flex flex-row gap-2 pr-2 last:hover:rounded-b-xl sm:pr-5", className)}
            data-testid={dataTestId}
        >
            {/* Step icon */}
            <div className="ml-3 flex w-[40px] min-w-[40px] flex-col items-center py-1 md:ml-[24px]">
                <StepIcon state={state} />
                {hasNext && (
                    <div className="mt-1 h-full w-0.5 grow border-r-[2.5px] border-dotted border-gray-600/20" />
                )}
            </div>

            {/* Step content */}
            <div className="flex min-w-0 grow flex-col">
                <div className="flex min-h-8 w-full flex-row items-center">
                    {/* Step text */}
                    <div className="flex flex-grow flex-col overflow-x-hidden">
                        {/* Title & subtitle */}
                        <div className="inline-flex grow gap-2">
                            <Heading1 className="shrink-0 text-base">{title}</Heading1>
                            {subtitle && (
                                <span className="min-w-0 truncate text-base text-content-secondary">{subtitle}</span>
                            )}
                        </div>
                        {/* Additional content */}
                        {hint && (
                            <div className="h-fit max-w-full text-left">
                                <Text className="text-wrap text-sm text-content-secondary">{hint}</Text>
                            </div>
                        )}
                        {secondLine && (
                            <div className="h-fit max-w-full">
                                <Text className="line-clamp-5 font-mono text-sm text-content-secondary">
                                    {secondLine}
                                </Text>
                            </div>
                        )}
                        <Collapsible open={state === StepState.Running && Boolean(logLine)}>
                            <CollapsibleContent>
                                <div
                                    className={cn(
                                        "mt-1 flex items-center justify-between gap-1",
                                        "rounded-md border-[0.5px] border-border-base bg-surface-pure px-2 py-2",
                                    )}
                                >
                                    <div className="flex items-start gap-3 overflow-x-hidden">
                                        {logLine?.timestamp && (
                                            <span className="min-w-fit font-mono text-sm text-content-tertiary">
                                                {formatMediumTime(logLine.timestamp)}
                                            </span>
                                        )}
                                        <Text className="truncate font-mono text-sm text-content-primary">
                                            {logLine?.line}
                                        </Text>
                                    </div>
                                    <Tooltip content="View logs">
                                        <Button
                                            LeadingIcon={IconMaximize}
                                            variant="secondary"
                                            size="sm"
                                            onClick={onLogLineClick}
                                            data-tracking-id="view-logs-logline-step"
                                        />
                                    </Tooltip>
                                </div>
                            </CollapsibleContent>
                        </Collapsible>
                    </div>
                    {/* Step actions */}
                    <div className="ml-2 flex gap-2">
                        {hoverActions && (
                            <VisibleOnActive
                                className={cn(
                                    "!mr-0.5", // we use important here because not-sr-only overrides margin-right to be 0. The margin is used to ensure focus rings of action buttons don't clip under the side padding
                                )}
                            >
                                {hoverActions}
                            </VisibleOnActive>
                        )}
                        {actions}
                    </div>
                </div>
                {/* Step bottom border */}
                {hasNext && <div className="my-[10px] border-t-[1.5px] border-dotted border-gray-600/20" />}
            </div>
        </div>
    );
};

const StepIcon = ({ state }: { state: StepState }) => {
    return (
        <div className="flex min-h-6 min-w-6 items-center justify-center">
            <StartSequenceStepIcon state={state} />
        </div>
    );
};

type SCMAuthenticationModalProps = {
    repoURL: string;
    authResponse: AuthenticatedWithRunnerResponse;
    clazz: PlainRunnerEnvironmentClass;
    onClose: () => void;
    onAuthSuccess: () => void;
    "data-track-location": TrackLocation;
};
const AuthenticationModal: FC<SCMAuthenticationModalProps> = (p) => {
    const patAuth = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.patAuth : undefined;
    }, [p]);
    const authUrl = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.url : "";
    }, [p]);
    const scmId = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.scmId : "";
    }, [p]);
    const scmName = useMemo(() => {
        return p.authResponse.type === "AuthenticationRequired" ? p.authResponse.scmName : "";
    }, [p]);

    return (
        <div>
            <SCMAuthenticationModal
                repoURL={p.repoURL}
                authenticationUrl={authUrl}
                patAuth={patAuth}
                scmId={scmId}
                scmName={scmName}
                onClose={p.onClose}
                onClickContinue={p.onAuthSuccess}
                runnerId={p.clazz.runnerId}
                data-track-location={p["data-track-location"]}
            />
        </div>
    );
};
